import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  FlatList,
  ViewToken,
  Animated,
  Easing,
  ActivityIndicator,
} from "react-native";
import {
  Nunito_400Regular,
  Nunito_600SemiBold,
} from "@expo-google-fonts/nunito";
import { useFonts, Raleway_700Bold } from "@expo-google-fonts/raleway";
import { LinearGradient } from "expo-linear-gradient";
import { router } from "expo-router";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";

import { onboardingSwiperData } from "../../../constants";
import i18n from "../../../locales/i18n";
import logoImage from "../../../assets/logo-no-bg-white.png";

interface OnboardingItem {
  id: number;
  title: string;
  description: string;
  image: any;
}

interface OnboardingSlideProps {
  item: OnboardingItem;
  index: number;
  isLastSlide: boolean;
}

interface PaginationDotsProps {
  data: OnboardingItem[];
  currentIndex: number;
}

interface ActionButtonsProps {
  onNext: () => void;
  currentIndex: number;
  totalSlides: number;
}

// Constants
const GRADIENT_COLORS: readonly [string, string] = ["#004E6D", "#0596D0"];
const GRADIENT_LOCATIONS = {
  start: { x: 0.1, y: 0.2 },
  end: { x: 0.9, y: 0.8 },
};

// Separate component for onboarding slides - memoized for performance
const OnboardingSlide: React.FC<OnboardingSlideProps> = React.memo(
  ({ item, index, isLastSlide }) => {
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(0.9)).current;
    const slideAnim = useRef(new Animated.Value(50)).current;

    useEffect(() => {
      // Reset animations
      fadeAnim.setValue(0);
      scaleAnim.setValue(0.9);
      slideAnim.setValue(50);

      // Start animations
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 600,
          useNativeDriver: true,
          easing: Easing.out(Easing.cubic),
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
          easing: Easing.out(Easing.cubic),
        }),
      ]).start();
    }, [index]);

    const slideContent = (
      <>
        <Animated.View
          className="items-center py-4"
          style={{
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }],
          }}
        >
          <View
            style={{
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.3,
              shadowRadius: 12,
              elevation: 10,
            }}
          >
            <Image
              source={logoImage}
              className="rounded-full"
              style={{
                width: wp("35%"),
                height: hp("10%"),
                resizeMode: "contain",
              }}
            />
          </View>
        </Animated.View>

        <Animated.View
          className="items-center mt-4"
          style={{
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }, { translateY: slideAnim }],
          }}
        >
          <Image
            source={item.image}
            className="rounded-full"
            style={[
              {
                width: wp("80%"),
                height: isLastSlide ? hp("30%") : hp("35%"),
                resizeMode: "contain",
              },
              {
                backgroundColor: item.id === 1 ? "white" : "transparent",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.2,
                shadowRadius: 15,
              },
            ]}
          />
        </Animated.View>

        <Animated.View
          style={{
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          }}
        >
          <Text
            className="text-white text-center mb-4"
            style={{
              fontFamily: "kawkabBold",
              fontSize: hp("2.8%"),
              paddingHorizontal: wp("5%"),
              textShadowColor: "rgba(0, 0, 0, 0.3)",
              textShadowOffset: { width: 0, height: 2 },
              textShadowRadius: 4,
            }}
          >
            {item.title}
          </Text>
        </Animated.View>

        <Animated.View
          className="px-5 mb-4"
          style={{
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          }}
        >
          <Text
            className="text-gray-200 text-center"
            style={{
              fontFamily: "kawkabReg",
              fontSize: hp("1.8%"),
              lineHeight: hp("3.2%"),
              textShadowColor: "rgba(0, 0, 0, 0.2)",
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 2,
            }}
          >
            {item.description}
          </Text>
        </Animated.View>

        {isLastSlide && <AuthButtons fadeAnim={fadeAnim} />}
      </>
    );

    return (
      <View style={{ width: wp("100%") }}>
        <LinearGradient
          colors={GRADIENT_COLORS}
          start={GRADIENT_LOCATIONS.start}
          end={GRADIENT_LOCATIONS.end}
          className="rounded-b-3xl"
          style={{
            width: wp("100%"),
            height: isLastSlide ? hp("120%") : hp("80%"),
            borderBottomLeftRadius: 120,
            borderBottomRightRadius: 120,
          }}
        >
          {slideContent}
        </LinearGradient>
      </View>
    );
  },
);

// Separate component for authentication buttons - memoized
const AuthButtons: React.FC<{ fadeAnim: Animated.Value }> = React.memo(
  ({ fadeAnim }) => {
    const isNavigating = useRef(false);
    const signupButtonScale = useRef(new Animated.Value(1)).current;
    const loginButtonScale = useRef(new Animated.Value(1)).current;

    const handleSignupPressIn = () => {
      Animated.spring(signupButtonScale, {
        toValue: 0.95,
        useNativeDriver: true,
      }).start();
    };

    const handleSignupPressOut = () => {
      Animated.spring(signupButtonScale, {
        toValue: 1,
        useNativeDriver: true,
      }).start();
    };

    const handleLoginPressIn = () => {
      Animated.spring(loginButtonScale, {
        toValue: 0.95,
        useNativeDriver: true,
      }).start();
    };

    const handleLoginPressOut = () => {
      Animated.spring(loginButtonScale, {
        toValue: 1,
        useNativeDriver: true,
      }).start();
    };

    return (
      <Animated.View
        className="px-8 space-y-4 flex flex-col gap-5"
        style={{
          opacity: fadeAnim,
        }}
      >
        <Animated.View style={{ transform: [{ scale: signupButtonScale }] }}>
          <TouchableOpacity
            onPress={async () => {
              if (isNavigating.current) return;
              isNavigating.current = true;
              await AsyncStorage.setItem("hasSeenOnboarding", "true");
              router.replace("/(routes)/(auth)/signup");
            }}
            onPressIn={handleSignupPressIn}
            onPressOut={handleSignupPressOut}
            style={{
              paddingVertical: hp("1.5%"),
              paddingHorizontal: wp("8%"),
              borderRadius: 30,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 6 },
              shadowOpacity: 0.3,
              shadowRadius: 12,
              elevation: 10,
            }}
            activeOpacity={0.85}
          >
            <Text
              className="text-center"
              style={{
                color: "#004E6D",
                fontSize: hp("2.2%"),
                fontFamily: "kawkabBold",
                fontWeight: "700",
              }}
            >
              {i18n.t("createAccount")}
            </Text>
          </TouchableOpacity>
        </Animated.View>

        <Animated.View style={{ transform: [{ scale: loginButtonScale }] }}>
          <TouchableOpacity
            onPress={async () => {
              if (isNavigating.current) return;
              isNavigating.current = true;
              await AsyncStorage.setItem("hasSeenOnboarding", "true");
              router.replace("/(routes)/(auth)/login");
            }}
            onPressIn={handleLoginPressIn}
            onPressOut={handleLoginPressOut}
            className="border-2 border-white"
            style={{
              paddingVertical: hp("1.5%"),
              paddingHorizontal: wp("8%"),
              borderRadius: 30,
              // backgroundColor: "transparent",
              // shadowColor: "#000",
              // shadowOffset: { width: 0, height: 4 },
              // shadowOpacity: 0.2,
              // shadowRadius: 8,
              // elevation: 6,
            }}
          >
            <Text
              className="text-white text-center"
              style={{
                fontSize: hp("2.2%"),
                fontFamily: "kawkabBold",
                fontWeight: "600",
              }}
            >
              {i18n.t("login")}
            </Text>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>
    );
  },
);

// Separate component for pagination dots - memoized
const PaginationDots: React.FC<PaginationDotsProps> = React.memo(
  ({ data, currentIndex }) => {
    const animatedValues = useRef(
      data.map(() => new Animated.Value(0)),
    ).current;

    useEffect(() => {
      animatedValues.forEach((anim, index) => {
        Animated.spring(anim, {
          toValue: index === currentIndex ? 1 : 0,
          useNativeDriver: false,
          friction: 6,
          tension: 50,
        }).start();
      });
    }, [currentIndex]);

    return (
      <View className="flex-row justify-center items-center py-6">
        {data.map((_, index) => {
          const width = animatedValues[index].interpolate({
            inputRange: [0, 1],
            outputRange: [wp("2.5%"), wp("8%")],
          });

          const backgroundColor = animatedValues[index].interpolate({
            inputRange: [0, 1],
            outputRange: ["rgb(209, 213, 219)", "rgb(15, 118, 110)"],
          });

          return (
            <Animated.View
              key={`dot-${index}`}
              className="rounded-full mx-1"
              style={{
                width,
                height: wp("2.5%"),
                backgroundColor,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: index === currentIndex ? 0.3 : 0,
                shadowRadius: 4,
                elevation: index === currentIndex ? 4 : 0,
              }}
            />
          );
        })}
      </View>
    );
  },
);

// Separate component for action buttons - memoized
const ActionButtons: React.FC<ActionButtonsProps> = React.memo(
  ({ onNext, currentIndex, totalSlides }) => {
    const buttonAnim = useRef(new Animated.Value(1)).current;
    const pulseAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
      // Pulse animation for "Next" button
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.05,
            duration: 1000,
            useNativeDriver: true,
            easing: Easing.inOut(Easing.ease),
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
            easing: Easing.inOut(Easing.ease),
          }),
        ]),
      );
      pulse.start();

      return () => pulse.stop();
    }, []);

    const handlePressIn = () => {
      Animated.spring(buttonAnim, {
        toValue: 0.95,
        useNativeDriver: true,
      }).start();
    };

    const handlePressOut = () => {
      Animated.spring(buttonAnim, {
        toValue: 1,
        useNativeDriver: true,
      }).start();
    };

    if (currentIndex >= totalSlides - 1) return null;

    return (
      <View className="absolute left-8 right-8" style={{ bottom: hp("4%") }}>
        <Animated.View
          style={{
            transform: [{ scale: Animated.multiply(buttonAnim, pulseAnim) }],
          }}
        >
          <TouchableOpacity
            onPress={onNext}
            onPressIn={handlePressIn}
            onPressOut={handlePressOut}
            activeOpacity={0.85}
          >
            <LinearGradient
              colors={GRADIENT_COLORS}
              start={GRADIENT_LOCATIONS.start}
              end={GRADIENT_LOCATIONS.end}
              className="justify-center items-center self-center"
              style={{
                width: wp("75%"),
                height: hp("6%"),
                borderRadius: 30,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.4,
                shadowRadius: 10,
                elevation: 10,
              }}
            >
              <Text
                className="text-white text-center"
                style={{
                  fontSize: hp("2%"),
                  fontFamily: "kawkabBold",
                  fontWeight: "600",
                  letterSpacing: 0.5,
                }}
              >
                {i18n.t("next")}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        </Animated.View>
      </View>
    );
  },
);

// Main component
const WelcomeIntroScreen: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);
  const scrollX = useRef(new Animated.Value(0)).current;

  // Font loading
  const [fontsLoaded, fontError] = useFonts({
    Raleway_700Bold,
    Nunito_400Regular,
    Nunito_600SemiBold,
  });

  // Handle next slide
  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < onboardingSwiperData.length) {
      setCurrentIndex(nextIndex);
      flatListRef.current?.scrollToIndex({
        index: nextIndex,
        animated: true,
      });
    }
  }, [currentIndex]);

  // Handle viewable items change
  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length > 0 && viewableItems[0].index !== null) {
        setCurrentIndex(viewableItems[0].index);
      }
    },
    [],
  );

  const viewabilityConfig = {
    itemVisiblePercentThreshold: 50,
  };

  // Render individual slide
  const renderSlide = useCallback(
    ({ item, index }: { item: OnboardingItem; index: number }) => (
      <OnboardingSlide
        item={item}
        index={index}
        isLastSlide={index === onboardingSwiperData.length - 1}
      />
    ),
    [],
  );

  // Get item layout for FlatList optimization
  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: wp("100%"),
      offset: wp("100%") * index,
      index,
    }),
    [],
  );

  // Show loading state if fonts aren't loaded
  if (!fontsLoaded && !fontError) {
    return (
      <View className="flex-1 justify-center items-center">
        <LinearGradient
          colors={GRADIENT_COLORS}
          start={GRADIENT_LOCATIONS.start}
          end={GRADIENT_LOCATIONS.end}
          style={{
            flex: 1,
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ActivityIndicator size="large" color="#ffffff" />
          <Text
            className="text-white mt-4"
            style={{
              fontFamily: "kawkabReg",
              fontSize: hp("2%"),
            }}
          >
            Loading...
          </Text>
        </LinearGradient>
      </View>
    );
  }

  return (
    <View className="flex-1 relative">
      <FlatList
        ref={flatListRef}
        data={onboardingSwiperData}
        renderItem={renderSlide}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        keyExtractor={(_, index) => `slide-${index}`}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        getItemLayout={getItemLayout}
        removeClippedSubviews
        maxToRenderPerBatch={2}
        windowSize={3}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { x: scrollX } } }],
          { useNativeDriver: false },
        )}
        scrollEventThrottle={16}
      />
      <PaginationDots data={onboardingSwiperData} currentIndex={currentIndex} />
      <ActionButtons
        onNext={handleNext}
        currentIndex={currentIndex}
        totalSlides={onboardingSwiperData.length}
      />
    </View>
  );
};

export default WelcomeIntroScreen;
